import Sortable from 'sortablejs';

/**
 * Alpine-component voor de bewerkmodus van het admin-dashboard.
 *
 * @param {{basePath: string, widgets: Object, meta: Object, autoEmptyKeys: string[],
 *          viewId: number|null, viewName: string, views: Array,
 *          defaultViewId: number|null, isOwner: boolean}} initial
 */
export function dashboardEditor(initial = {}) {
  return {
    basePath: initial.basePath || '/admin/dashboard',
    editMode: false,
    activeGroup: '',
    saving: false,
    widgets: initial.widgets || {},
    meta: initial.meta || {},
    autoEmptyKeys: initial.autoEmptyKeys || [],
    viewId: initial.viewId || null,
    viewName: initial.viewName || '',
    views: initial.views || [],
    defaultViewId: initial.defaultViewId || null,
    isOwner: initial.isOwner || false,
    renaming: false,
    renameValue: initial.viewName || '',
    showNewViewForm: false,
    newViewName: '',
    _sortable: null,
    _snapshot: null,
    _order: [],

    init() {
      if (initial.autoEdit) this.enterEdit();
    },

    widgetEnabled(key) {
      return !!(this.widgets[key] && this.widgets[key].enabled);
    },

    widgetIsAutoEmpty(key) {
      return this.autoEmptyKeys.includes(key);
    },

    widgetWidth(key) {
      return (this.widgets[key] && this.widgets[key].width) || 'full';
    },

    setWidth(key, width) {
      if (this.widgets[key]) this.widgets[key].width = width;
    },

    toggleWidget(key) {
      if (this.widgets[key]) this.widgets[key].enabled = !this.widgets[key].enabled;
    },

    domOrder() {
      return Array.from(this.$refs.grid.children)
        .map((el) => el.dataset.key)
        .filter(Boolean);
    },

    enterEdit() {
      this.activeGroup = '';
      this._snapshot = JSON.parse(JSON.stringify(this.widgets));
      this._order = this.domOrder();
      this.editMode = true;
      this.renameValue = this.viewName;
      this.$nextTick(() => {
        this._sortable = Sortable.create(this.$refs.grid, {
          handle: '.dash-drag-handle',
          draggable: '.dash-widget',
          animation: 150,
          ghostClass: 'opacity-40',
        });
      });
    },

    _teardown() {
      if (this._sortable) {
        this._sortable.destroy();
        this._sortable = null;
      }
      this.editMode = false;
      this.renaming = false;
      this.showNewViewForm = false;
      this.newViewName = '';
    },

    cancel() {
      if (this._snapshot) this.widgets = this._snapshot;
      const grid = this.$refs.grid;
      this._order.forEach((key) => {
        const el = grid.querySelector(`[data-key="${key}"]`);
        if (el) grid.appendChild(el);
      });
      this._teardown();
    },

    _payload() {
      return this.domOrder().map((key) => ({
        key,
        enabled: this.widgetEnabled(key),
        width: this.widgetWidth(key),
      }));
    },

    _csrf() {
      return document.querySelector('meta[name="csrf-token"]')?.content || '';
    },

    _toast(message, type = 'info') {
      window.dispatchEvent(new CustomEvent('toast', { detail: { message, type } }));
    },

    async _post(url, body = {}) {
      const params = new URLSearchParams({ ...body });
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this._csrf(),
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: params,
      });
      return res.json().catch(() => ({}));
    },

    async save() {
      if (this.saving) return;
      this.saving = true;
      try {
        const data = await this._post(`${this.basePath}/layout`, {
          layout: JSON.stringify(this._payload()),
        });
        if (data.ok) {
          this._teardown();
          window.location.reload();
        } else {
          this._toast('Opslaan mislukt. Probeer het opnieuw.', 'error');
        }
      } catch {
        this._toast('Opslaan mislukt. Probeer het opnieuw.', 'error');
      } finally {
        this.saving = false;
      }
    },

    resetDefault() {
      this.$dispatch('open-confirm', {
        title: 'Standaard herstellen?',
        message: 'De layout van de actieve view wordt teruggezet naar de standaardindeling.',
        onConfirm: () => this._doReset(),
      });
    },

    async _doReset() {
      try {
        const data = await this._post(`${this.basePath}/layout/reset`);
        if (data.ok) {
          window.location.reload();
        } else {
          this._toast('Herstellen mislukt.', 'error');
        }
      } catch {
        this._toast('Herstellen mislukt.', 'error');
      }
    },

    // ── View management ──────────────────────────────────────────────────

    async activateView(id) {
      try {
        const data = await this._post(`${this.basePath}/views/${id}/activate`);
        if (data.ok && data.reload) window.location.reload();
        else if (!data.ok) this._toast('Wisselen mislukt.', 'error');
      } catch {
        this._toast('Wisselen mislukt.', 'error');
      }
    },

    async renameView() {
      if (!this.viewId) return;
      const naam = this.renameValue.trim();
      if (!naam) return;
      try {
        const data = await this._post(`${this.basePath}/views/${this.viewId}/update`, { naam });
        if (data.ok) {
          this.viewName = naam;
          this.renaming = false;
          const v = this.views.find((v) => v.id === this.viewId);
          if (v) v.naam = naam;
          this._toast('View hernoemd.', 'success');
        } else {
          this._toast('Hernoemen mislukt.', 'error');
        }
      } catch {
        this._toast('Hernoemen mislukt.', 'error');
      }
    },

    async setAsDefault() {
      if (!this.viewId || this.viewId === this.defaultViewId) return;
      try {
        const data = await this._post(`${this.basePath}/views/${this.viewId}/set-default`);
        if (data.ok) {
          this.defaultViewId = this.viewId;
          this.views.forEach((v) => { v.is_default = v.id === this.viewId; });
          this._toast('Ingesteld als standaard.', 'success');
        } else {
          this._toast('Mislukt.', 'error');
        }
      } catch {
        this._toast('Mislukt.', 'error');
      }
    },

    async setGlobalDefault() {
      if (!this.viewId) return;
      try {
        const data = await this._post(`${this.basePath}/views/${this.viewId}/set-global-default`);
        if (data.ok) {
          this._toast('Globale standaard ingesteld.', 'success');
        } else {
          this._toast('Niet toegestaan.', 'error');
        }
      } catch {
        this._toast('Mislukt.', 'error');
      }
    },

    async createView() {
      const naam = this.newViewName.trim();
      if (!naam) return;
      try {
        const data = await this._post(`${this.basePath}/views`, {
          naam,
          layout: JSON.stringify(this._payload()),
        });
        if (data.ok && data.reload) {
          window.location.reload();
        } else if (!data.ok) {
          this._toast('Aanmaken mislukt.', 'error');
        }
      } catch {
        this._toast('Aanmaken mislukt.', 'error');
      }
    },

    deleteCurrentView() {
      if (!this.viewId || this.views.length <= 1) return;
      this.$dispatch('open-confirm', {
        title: 'View verwijderen?',
        message: `"${this.viewName}" wordt permanent verwijderd.`,
        onConfirm: () => this._doDeleteView(),
      });
    },

    async _doDeleteView() {
      try {
        const data = await this._post(`${this.basePath}/views/${this.viewId}/delete`);
        if (data.ok && data.reload) {
          window.location.reload();
        } else if (data.error === 'last_view') {
          this._toast('Je kunt de laatste view niet verwijderen.', 'error');
        } else {
          this._toast('Verwijderen mislukt.', 'error');
        }
      } catch {
        this._toast('Verwijderen mislukt.', 'error');
      }
    },
  };
}
